import { isTourFlightStepAvailable } from './tourFlightSelectors'
import { createSelector } from 'reselect'
import { findPostPurchaseCheckout, isAccommodationV2Item, isBedbankItem, isInstantBookingLEHotelItem, isPurchaseCreditItem } from 'lib/checkout/checkoutUtils'
import { areAccommodationFlightDatesWithinBookingWindow, canShowAddFlightsForAccommodationItems, checkoutAccommodationOfferView } from './view/accommodation'
import { getFlightItems, getIsCharterFlightCheckout, getNonCharterFlightItems } from './view/flights'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getCanRedeemLuxPlusBenefits, getIsFlightsLuxPlusPricingEligibleSpoofedNonMember, getIsFreePreviewLuxPlusMember, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { hasBundledAccommodationRate } from './hasBundledAccommodationRate'
import { getTourV1Items } from './view/tourv1'
import { checkIsExperienceGiftOfferOrderMode } from 'checkout/selectors/view/checkIsExperienceGiftOfferOrderMode'
import { getFunnelFeatures } from './funnel'
import { FLIGHTS_DISABLED_GEO_STATES } from 'constants/config/region'
import { getIsInternationalCheckoutFlowTreatment } from './internationalCheckoutFlow'
import { ChertoExperiments, Experiments } from 'constants/experiments'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation, isAllowedToTriggerExperiment } from 'lib/optimizely/optimizelyUtils'
import getExperienceItems from './view/items/getExperienceItems'
import { getIsMobileCheckoutUpsellHideAudience, getIsMobileCheckoutUpsellHideTreatment } from './mobileCheckoutUpsellHideSelectors'
import config from 'constants/config'
import {
  getHasBothTransferDirectionsBundled,
  isOneClickExperienceTransferEnabled,
} from './experienceSelectors'
import { getCheckoutFlightOccupancies } from './flightSearchSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { maxFlightPassengerCount } from 'constants/flight'
import getUnifiedTransferView from './view/itemViews/getUnifiedTransferView'

const getHasPaidLuxPlusMembershipInAnyRegion = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (enabled, memberSubscriptionItem): boolean => {
    return enabled &&
      (memberSubscriptionItem?.status === 'ACTIVE' || memberSubscriptionItem?.status === 'AWAITING_RENEWAL') &&
      memberSubscriptionItem?.tier !== 'free_preview'
  },
)

const getHasSelectedFlightsLuxPlusUpsellContext = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (flightItems): boolean => flightItems.length > 0,
)

export const isLuxPlusCheckoutStepAvailable = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => getHasPaidLuxPlusMembershipInAnyRegion(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => checkIsExperienceGiftOfferOrderMode(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.checkout.cart.items.some(isPurchaseCreditItem),
  (state: App.State) => getIsFreePreviewLuxPlusMember(state),
  (state: App.State) => getIsFlightsLuxPlusPricingEligibleSpoofedNonMember(state),
  (state: App.State) => getHasSelectedFlightsLuxPlusUpsellContext(state),
  (enabled, canRedeemLuxPlusBenefits, hasPaidLuxPlusMembershipInAnyRegion, isGift, isPostPurchase, isExperienceGiftOfferOrderMode, funnelFeatures, hasPurchaseCreditItem, isFreePreviewLuxPlusMember, isFlightsLuxPlusPricingEligibleSpoofedNonMember, hasSelectedFlightsLuxPlusUpsellContext): boolean => {
    if (hasPurchaseCreditItem) return false
    const shouldSeeUpsell = !canRedeemLuxPlusBenefits && !hasPaidLuxPlusMembershipInAnyRegion
    const isFunnelEligible = funnelFeatures.luxPlus ||
      isFreePreviewLuxPlusMember ||
      isFlightsLuxPlusPricingEligibleSpoofedNonMember ||
      hasSelectedFlightsLuxPlusUpsellContext
    return !!(enabled && shouldSeeUpsell && (isExperienceGiftOfferOrderMode || !isGift) && !isPostPurchase && isFunnelEligible)
  },
)

export const isFlightsPendingToAddToCart = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (flightItems): boolean => {
    return flightItems.length > 0 &&
      flightItems.some(item => {
        if (item.fareType === 'return') {
          return !item.flights[0] || !item.flights[1]
        } else if (item.fareType === 'oneWay') {
          return !item.flights[0]
        }
      })
  },
)

export const hasExistingFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems): boolean => flightItems.length > 0,
)

export const hasIncompleteTourItem = createSelector(
  (state: App.State) => getTourV1Items(state),
  item => item.some(item => !item.packageId || !item.startDate || !item.endDate),
)

export const hasInsuranceItem = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (items) => {
    if (!items?.length) {
      return false
    }

    return items.some(item => item.status !== 'cancelled')
  },
)

const getExperiencesUpsellWouldShow = createSelector(
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => hasBundledAccommodationRate(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getExperimentEnabledDecision(getOptimizelyExperimentVariation(state, Experiments.standaloneFlightUpsellExperiences.id)),
  (isInternationalCheckoutFlowTreatment, accommodationViews, villaViews, hasBundledRate, postPurchase, funnelFeatures, flightItems, standaloneFlightUpsellExperiences): boolean => {
    if (isInternationalCheckoutFlowTreatment) {
      return false
    }

    if (!funnelFeatures.experiences) {
      return false
    }

    // Experience-only BNBL should skip ExperiencesPage (only used for adding experiences to hotel checkout)
    const hasAccommodation = accommodationViews.data.length > 0 || villaViews.data.length > 0
    if (postPurchase === 'select-date' && !hasAccommodation) {
      return false
    }

    if (!postPurchase || postPurchase === 'select-date') {
      if (hasAccommodation) {
        return accommodationViews.data.every(view => view.itemViews.every(itemView => isInstantBookingLEHotelItem(itemView.item) || isBedbankItem(itemView.item) || isAccommodationV2Item(itemView.item))) ||
          villaViews.data.length > 0
      }

      // Upsell experience on flight standalone flow
      if (flightItems.length) {
        return config.EXPERIENCES_STANDALONE_FLIGHTS_INFLOW_ENABLED && standaloneFlightUpsellExperiences
      }
    }

    return false
  },
)

/**
 * Variant hides the experiences step only while the cart has nothing to edit.
 * An experience already in the cart keeps the step.
 */
export const isExperiencesSupportedToAddToCart = createSelector(
  (state: App.State) => getExperiencesUpsellWouldShow(state),
  (state: App.State) => getIsMobileCheckoutUpsellHideTreatment(state),
  (state: App.State) => getExperienceItems(state),
  (wouldShow, hideExperiences, experienceItems): boolean =>
    wouldShow && !(hideExperiences && experienceItems.length === 0),
)

export const isAirportTransfersStepAvailable = createSelector(
  (state: App.State) => isOneClickExperienceTransferEnabled(state),
  (state: App.State) => getUnifiedTransferView(state),
  (state: App.State) => getHasBothTransferDirectionsBundled(state),
  (state: App.State) => getFunnelFeatures(state),
  (
    isOneClickTransferEnabled,
    unifiedTransferView,
    hasBothDirectionsBundled,
    funnelFeatures,
  ): boolean => {
    if (!config.EXPERIENCE_TRANSFER_DEDICATED_PAGE || !funnelFeatures.experiences) return false

    const hasLoyaltyTransfers = unifiedTransferView.hasRequiredData &&
      unifiedTransferView.data.transferItems.length > 0 &&
      unifiedTransferView.data.transferType !== 'HOME_TO_AIRPORT'

    return isOneClickTransferEnabled &&
      !hasLoyaltyTransfers &&
      !hasBothDirectionsBundled
  },
)

export const isLuxPlusAvailableToAddToCart = createSelector(
  (state: App.State) => isLuxPlusCheckoutStepAvailable(state),
  (state: App.State) => state.auth.account.luxPlus.member.disableMembership,
  (isLuxPlusStepAvailable, disableMembership): boolean => isLuxPlusStepAvailable && !disableMembership,
)

/**
 * Maldives charter bundles run the LuxPlus+ upsell BEFORE feeder flight selection,
 * inverting the standard flights-before-LuxPlus+ ordering for this funnel only.
 *
 * Deliberately keyed on the charter item alone, NOT on whether a feeder is still pending:
 * a pending-based predicate would flip to false the moment the feeder legs are chosen, and
 * the post-flights branch in `getNextCheckoutPage` would then send the customer back to a
 * step they have already seen.
 *
 * `App.CharterBundleRoute` only covers Maldives routes today. If a non-Maldives charter
 * route is ever added, narrow this to the campaign-tag check in `view/flights.ts:485`.
 *
 * Both the routing (`getNextCheckoutPage`) and the progress stepper
 * (`checkoutStepsSelectors`) read the ordering decision from here, so they cannot drift.
 */
export const getShouldShowLuxPlusBeforeCharterFeederFlights = getIsCharterFlightCheckout

export const isLuxPlusStandaloneStepAvailable = createSelector(
  (state: App.State) => isLuxPlusCheckoutStepAvailable(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (isLuxPlusAvailable, isInternationalCheckoutFlowTreatment): boolean =>
    isLuxPlusAvailable && !isInternationalCheckoutFlowTreatment,
)

const getAddFlightsStepAvailability = createSelector(
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => canShowAddFlightsForAccommodationItems(state),
  (state: App.State) => isFlightsPendingToAddToCart(state),
  (state: App.State) => hasExistingFlightItems(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => getCheckoutFlightOccupancies(state),
  (state: App.State) => areAccommodationFlightDatesWithinBookingWindow(state),
  (state: App.State) => state.geo.currentRegionHasFlights,
  (isInternationalCheckoutFlowTreatment, canShowAddFlightsForAccommodationItems, hasFlightsPending, hasExistingFlightItems, isGift, isPostPurchase, funnelFeatures, geoStateName, flightOccupants, datesWithinBookingWindow, currentRegionHasFlights) => {
    const passesCommonGates = !!currentRegionHasFlights &&
      datesWithinBookingWindow &&
      sumUpOccupancies([flightOccupants]) <= maxFlightPassengerCount &&
      !isInternationalCheckoutFlowTreatment &&
      !(geoStateName && FLIGHTS_DISABLED_GEO_STATES.includes(geoStateName)) &&
      !isGift &&
      !isPostPurchase &&
      !!funnelFeatures.flights
    const optionalUpsellWouldShow = passesCommonGates &&
      canShowAddFlightsForAccommodationItems &&
      !hasExistingFlightItems &&
      !hasFlightsPending
    const ignoringHide = passesCommonGates &&
      (hasFlightsPending || (canShowAddFlightsForAccommodationItems && !hasExistingFlightItems))

    return { ignoringHide, optionalUpsellWouldShow }
  },
)

/**
 * Variant hides only the optional Add Flights merchandising step.
 * A hotel+flights package, charter feeder, or any other pending flight still continues to Departing/Returning.
 * Bundled and tour flight routes stay on `isAddFlightsStepRouteAvailable`.
 */
export const isAddFlightsStepAvailable = createSelector(
  (state: App.State) => getAddFlightsStepAvailability(state),
  (state: App.State) => getIsMobileCheckoutUpsellHideTreatment(state),
  ({ ignoringHide, optionalUpsellWouldShow }, hideOptionalAddFlights): boolean =>
    hideOptionalAddFlights ? ignoringHide && !optionalUpsellWouldShow : ignoringHide,
)

/** Cart eligibility before treatment, independent of audience and assignment. */
export const getIsMobileCheckoutUpsellHideEligibleCart = createSelector(
  (state: App.State) => getExperiencesUpsellWouldShow(state),
  (state: App.State) => getAddFlightsStepAvailability(state),
  (experiencesWouldShow, { optionalUpsellWouldShow }): boolean =>
    experiencesWouldShow || optionalUpsellWouldShow,
)

/** Both arms activate for eligible mobile checkouts, before the variant is applied. */
export const getShouldActivateMobileCheckoutUpsellHide = createSelector(
  (state: App.State) => getIsMobileCheckoutUpsellHideAudience(state),
  (state: App.State) => getIsMobileCheckoutUpsellHideEligibleCart(state),
  (state: App.State) => isAllowedToTriggerExperiment(
    true,
    state.utm.content,
    ChertoExperiments.croMobileCheckoutHideExperiencesFlights.id,
  ),
  (audience, eligibleCart, canTrigger): boolean => audience && canTrigger && eligibleCart,
)

export const hasBundledFlightItem = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (flightItems): boolean => flightItems.some(item => !!item.bundledItemIds?.length),
)

export const isBundledAddFlightsStepAvailable = createSelector(
  (state: App.State) => getIsCharterFlightCheckout(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => hasBundledFlightItem(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => getCheckoutFlightOccupancies(state),
  (state: App.State) => areAccommodationFlightDatesWithinBookingWindow(state),
  (state: App.State) => state.geo.currentRegionHasFlights,
  (isCharterFlightCheckout, isInternationalCheckoutFlowTreatment, hasBundledFlightItem, isGift, isPostPurchase, funnelFeatures, geoStateName, flightOccupants, datesWithinBookingWindow, currentRegionHasFlights): boolean => {
    if (!currentRegionHasFlights) return false

    if (!datesWithinBookingWindow) return false

    if (sumUpOccupancies([flightOccupants]) > maxFlightPassengerCount) return false

    if (isCharterFlightCheckout) return false

    if (isInternationalCheckoutFlowTreatment) {
      return false
    }

    if (geoStateName && FLIGHTS_DISABLED_GEO_STATES.includes(geoStateName)) return false

    return hasBundledFlightItem && !isGift && !isPostPurchase && funnelFeatures.flights
  },
)

export const isAddFlightsStepRouteAvailable = createSelector(
  (state: App.State) => isAddFlightsStepAvailable(state),
  (state: App.State) => isBundledAddFlightsStepAvailable(state),
  isTourFlightStepAvailable,
  (isAddFlightsAvailable, isBundledFlightsAvailable, isTourFlightsAvailable): boolean =>
    isAddFlightsAvailable || isBundledFlightsAvailable || isTourFlightsAvailable,
)
